import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { PenTool, CheckCircle2, Loader2, ExternalLink, Eye, EyeOff, Info, Save, ChevronDown, ChevronUp } from 'lucide-react'
import { toast } from 'sonner'
import { esigningApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { usePermissions } from '@/contexts/PermissionContext'
import type { ESigningProviderType, UpdateESigningSettingsRequest } from '@/types'

// Provider logos/icons mapping
const PROVIDER_ICONS: Record<ESigningProviderType, string> = {
  builtin: '📝',
  docusign: '📄',
  leegality: '🇮🇳',
}

export default function ESigningSettingsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { canAddEdit } = usePermissions()
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({})
  const [formData, setFormData] = useState<UpdateESigningSettingsRequest>({
    provider: 'builtin',
    enabled: true,
    test_mode: true,
    settings: {},
  })
  const [hasChanges, setHasChanges] = useState(false)
  const [showSetupGuide, setShowSetupGuide] = useState(true)

  // Fetch available providers
  const { data: providers = [], isLoading: loadingProviders } = useQuery({
    queryKey: ['esigning-providers'],
    queryFn: esigningApi.getProviders,
  })

  // Fetch current settings
  const { data: currentSettings, isLoading: loadingSettings } = useQuery({
    queryKey: ['esigning-settings'],
    queryFn: esigningApi.getSettings,
  })

  // Update form when settings are loaded
  useEffect(() => {
    if (currentSettings) {
      setFormData({
        provider: currentSettings.provider || 'builtin',
        enabled: currentSettings.enabled ?? true,
        test_mode: currentSettings.test_mode ?? true,
        settings: currentSettings.settings || {},
      })
      setHasChanges(false)
    }
  }, [currentSettings])

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: esigningApi.updateSettings,
    onSuccess: () => {
      toast.success(t('esigning.settingsSaved', 'E-signing settings saved successfully'))
      queryClient.invalidateQueries({ queryKey: ['esigning-settings'] })
      setHasChanges(false)
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error))
    },
  })

  // Test connection mutation
  const testMutation = useMutation({
    mutationFn: esigningApi.testConnection,
    onSuccess: (result) => {
      if (result.success) {
        toast.success(t('esigning.connectionSuccess', 'Connection successful!'))
      } else {
        toast.error(result.error || t('esigning.connectionFailed', 'Connection failed'))
      }
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error))
    },
  })

  const selectedProvider = providers.find(p => p.provider === formData.provider)

  const handleProviderChange = (provider: ESigningProviderType) => {
    setFormData(prev => ({
      ...prev,
      provider,
      settings: {}, // Reset settings when changing provider
    }))
    setHasChanges(true)
  }

  const handleSettingChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      settings: { ...prev.settings, [key]: value },
    }))
    setHasChanges(true)
  }

  const handleEnabledChange = (enabled: boolean) => {
    setFormData(prev => ({ ...prev, enabled }))
    setHasChanges(true)
  }

  const handleSave = () => {
    saveMutation.mutate(formData)
  }

  const handleTestConnection = () => {
    // First save, then test
    if (hasChanges) {
      saveMutation.mutate(formData, {
        onSuccess: () => {
          testMutation.mutate()
        },
      })
    } else {
      testMutation.mutate()
    }
  }

  const toggleSecretVisibility = (key: string) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const isLoading = loadingProviders || loadingSettings
  const canEdit = canAddEdit('AGENCY_CONFIG')

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <PenTool className="h-6 w-6" />
            {t('esigning.title', 'E-Signing Settings')}
          </h1>
          <p className="text-muted-foreground">
            {t('esigning.description', 'Configure your preferred e-signing provider for contracts')}
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={handleSave}
            disabled={saveMutation.isPending || !hasChanges}
            variant={hasChanges ? 'default' : 'outline'}
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {t('common.save', 'Save')}
          </Button>
        )}
      </div>

      {/* Provider Selection */}
      <Card>
        <CardHeader>
          <CardTitle>{t('esigning.selectProvider', 'Select E-Signing Provider')}</CardTitle>
          <CardDescription>
            {t('esigning.selectProviderDesc', 'Choose the e-signing service to use for your contracts')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            {providers.map((provider) => (
              <div
                key={provider.provider}
                onClick={() => canEdit && handleProviderChange(provider.provider)}
                className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.provider === provider.provider
                    ? 'border-primary bg-primary/5'
                    : 'border-muted hover:border-muted-foreground/50'
                } ${!canEdit ? 'cursor-not-allowed opacity-60' : ''}`}
              >
                {formData.provider === provider.provider && (
                  <CheckCircle2 className="absolute top-2 right-2 h-5 w-5 text-primary" />
                )}
                <div className="text-2xl mb-2">{PROVIDER_ICONS[provider.provider]}</div>
                <h3 className="font-semibold">{provider.display_name}</h3>
                <p className="text-sm text-muted-foreground mt-1">{provider.description}</p>
                {provider.region_focus && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    <Badge variant="secondary" className="text-xs">
                      {provider.region_focus}
                    </Badge>
                  </div>
                )}
                {provider.docs_url && (
                  <a
                    href={provider.docs_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-xs text-primary mt-2 hover:underline"
                  >
                    {t('esigning.viewDocs', 'View Documentation')}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Enable/Disable Toggle and Test/Live Mode */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('esigning.enableEsigning', 'Enable E-Signing')}</CardTitle>
              <CardDescription>
                {t('esigning.enableEsigningDesc', 'Turn e-signing on or off for your agency')}
              </CardDescription>
            </div>
            <Switch
              checked={formData.enabled}
              onCheckedChange={handleEnabledChange}
              disabled={!canEdit}
            />
          </div>
        </CardHeader>
        {formData.enabled && formData.provider !== 'builtin' && (
          <CardContent className="pt-0">
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${formData.test_mode ? 'bg-yellow-500' : 'bg-green-500'}`} />
                <div>
                  <p className="font-medium">
                    {formData.test_mode
                      ? t('esigning.testMode', 'Test Mode')
                      : t('esigning.liveMode', 'Live Mode')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formData.test_mode
                      ? t('esigning.testModeDesc', 'Using sandbox/demo environment. No real signatures will be processed.')
                      : t('esigning.liveModeDesc', 'Using production environment. Real signatures will be processed.')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-sm ${formData.test_mode ? 'text-muted-foreground' : 'font-medium'}`}>
                  {t('esigning.test', 'Test')}
                </span>
                <Switch
                  checked={!formData.test_mode}
                  onCheckedChange={(checked) => {
                    setFormData(prev => ({ ...prev, test_mode: !checked }))
                    setHasChanges(true)
                  }}
                  disabled={!canEdit}
                />
                <span className={`text-sm ${!formData.test_mode ? 'text-muted-foreground' : 'font-medium'}`}>
                  {t('esigning.live', 'Live')}
                </span>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* DocuSign Setup Guide */}
      {formData.provider === 'docusign' && (
        <Card>
          <Collapsible open={showSetupGuide} onOpenChange={setShowSetupGuide}>
            <CardHeader className="cursor-pointer" onClick={() => setShowSetupGuide(!showSetupGuide)}>
              <CollapsibleTrigger asChild>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Info className="h-5 w-5 text-blue-500" />
                      {t('esigning.docusign.setupGuide', 'DocuSign Integration Setup Guide')}
                    </CardTitle>
                    <CardDescription>
                      {t('esigning.docusign.setupGuideDesc', 'Follow these steps to configure DocuSign integration')}
                    </CardDescription>
                  </div>
                  {showSetupGuide ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
              </CollapsibleTrigger>
            </CardHeader>
            <CollapsibleContent>
              <CardContent className="space-y-6">
                {/* Step 1 */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold">1</div>
                    <h4 className="font-semibold">{t('esigning.docusign.step1Title', 'Create a DocuSign Developer Account')}</h4>
                  </div>
                  <div className="ml-9 space-y-2">
                    <p className="text-sm text-muted-foreground">
                      {t('esigning.docusign.step1Desc', 'If you don\'t have a DocuSign account, create a free developer account to get started.')}
                    </p>
                    <Button variant="outline" size="sm" asChild>
                      <a href="https://developers.docusign.com/" target="_blank" rel="noopener noreferrer">
                        {t('esigning.docusign.createAccount', 'Create Developer Account')}
                        <ExternalLink className="h-3 w-3 ml-2" />
                      </a>
                    </Button>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold">2</div>
                    <h4 className="font-semibold">{t('esigning.docusign.step2Title', 'Create an Application in DocuSign Admin')}</h4>
                  </div>
                  <div className="ml-9 space-y-2">
                    <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                      <li>{t('esigning.docusign.step2a', 'Go to DocuSign Admin → Settings → Apps and Keys')}</li>
                      <li>{t('esigning.docusign.step2b', 'Click "Add App and Integration Key"')}</li>
                      <li>{t('esigning.docusign.step2c', 'Give your app a name (e.g., "Blueprint Desk")')}</li>
                      <li>{t('esigning.docusign.step2d', 'Select "Authorization Code Grant" and "Service Integration"')}</li>
                      <li>{t('esigning.docusign.step2e', 'Copy the Integration Key (Client ID) - you\'ll need this below')}</li>
                    </ol>
                    <Button variant="outline" size="sm" asChild>
                      <a href="https://admindemo.docusign.com/apps-and-keys" target="_blank" rel="noopener noreferrer">
                        {t('esigning.docusign.openAppsAndKeys', 'Open Apps and Keys (Demo)')}
                        <ExternalLink className="h-3 w-3 ml-2" />
                      </a>
                    </Button>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold">3</div>
                    <h4 className="font-semibold">{t('esigning.docusign.step3Title', 'Generate RSA Key Pair')}</h4>
                  </div>
                  <div className="ml-9 space-y-2">
                    <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                      <li>{t('esigning.docusign.step3a', 'In your app settings, scroll to "Service Integration"')}</li>
                      <li>{t('esigning.docusign.step3b', 'Click "Generate RSA" to create a new key pair')}</li>
                      <li>{t('esigning.docusign.step3c', 'IMPORTANT: Copy and save the Private Key immediately - it won\'t be shown again!')}</li>
                      <li>{t('esigning.docusign.step3d', 'Paste the entire private key (including -----BEGIN RSA PRIVATE KEY-----) in the field below')}</li>
                    </ol>
                    <Alert className="mt-2">
                      <Info className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        {t('esigning.docusign.privateKeyNote', 'The private key should start with "-----BEGIN RSA PRIVATE KEY-----" and end with "-----END RSA PRIVATE KEY-----"')}
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold">4</div>
                    <h4 className="font-semibold">{t('esigning.docusign.step4Title', 'Get Your User ID and Account ID')}</h4>
                  </div>
                  <div className="ml-9 space-y-2">
                    <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                      <li>{t('esigning.docusign.step4a', 'In Apps and Keys, find "API Account ID" - this is your Account ID')}</li>
                      <li>{t('esigning.docusign.step4b', 'Under "My Account Information", find "API Username" - this is your User ID')}</li>
                      <li>{t('esigning.docusign.step4c', 'Both values are GUIDs (e.g., 12345678-1234-1234-1234-123456789012)')}</li>
                    </ol>
                  </div>
                </div>

                {/* Step 5 */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold">5</div>
                    <h4 className="font-semibold">{t('esigning.docusign.step5Title', 'Grant User Consent')}</h4>
                  </div>
                  <div className="ml-9 space-y-2">
                    <p className="text-sm text-muted-foreground">
                      {t('esigning.docusign.step5Desc', 'After entering your credentials and saving, you need to grant consent for the app to impersonate your user. Click the button below to open the consent page:')}
                    </p>
                    {formData.settings.integration_key ? (
                      <div className="space-y-2">
                        <Button
                          variant="outline"
                          size="sm"
                          asChild
                        >
                          <a
                            href={`https://${formData.test_mode ? 'account-d' : 'account'}.docusign.com/oauth/auth?response_type=code&scope=signature%20impersonation&client_id=${formData.settings.integration_key}&redirect_uri=https://localhost/callback`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t('esigning.docusign.grantConsent', 'Grant Consent')}
                            <ExternalLink className="h-3 w-3 ml-2" />
                          </a>
                        </Button>
                        <p className="text-xs text-muted-foreground">
                          {t('esigning.docusign.consentNote', 'Log in with your DocuSign account and click "Accept". You may see an error page after - that\'s OK, consent is already granted.')}
                        </p>
                      </div>
                    ) : (
                      <Alert>
                        <Info className="h-4 w-4" />
                        <AlertDescription className="text-xs">
                          {t('esigning.docusign.enterIntegrationKey', 'Enter your Integration Key below first, then the consent link will appear here.')}
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                </div>

                {/* Step 6 */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold">6</div>
                    <h4 className="font-semibold">{t('esigning.docusign.step6Title', 'Test the Connection')}</h4>
                  </div>
                  <div className="ml-9 space-y-2">
                    <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                      <li>{t('esigning.docusign.step6a', 'Fill in all the fields below with your DocuSign credentials')}</li>
                      <li>{t('esigning.docusign.step6b', 'Click "Save" to save your settings')}</li>
                      <li>{t('esigning.docusign.step6c', 'Click "Test Connection" to verify everything is working')}</li>
                      <li>{t('esigning.docusign.step6d', 'If you see "Connection successful!", you\'re all set!')}</li>
                    </ol>
                  </div>
                </div>

                {/* Common Errors */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">{t('esigning.docusign.commonErrors', 'Common Errors & Solutions')}</h4>
                  <div className="space-y-3 text-sm">
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="font-medium text-destructive">consent_required</p>
                      <p className="text-muted-foreground">{t('esigning.docusign.errorConsent', 'You need to grant consent. Click the "Grant Consent" button in Step 5 above.')}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="font-medium text-destructive">invalid_grant - issuer_not_found</p>
                      <p className="text-muted-foreground">{t('esigning.docusign.errorIssuer', 'The Integration Key is incorrect or the app doesn\'t exist. Double-check your Integration Key.')}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="font-medium text-destructive">failed to parse private key</p>
                      <p className="text-muted-foreground">{t('esigning.docusign.errorPrivateKey', 'The private key format is invalid. Make sure you copied the entire key including the BEGIN and END lines.')}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>
      )}

      {/* Provider Configuration */}
      {selectedProvider && selectedProvider.provider !== 'builtin' && selectedProvider.fields.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>
              {t('esigning.configureProvider', 'Configure {{provider}}', { provider: selectedProvider.display_name })}
            </CardTitle>
            <CardDescription>
              {t('esigning.configureProviderDesc', 'Enter your API credentials and settings')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedProvider.fields.map((field) => (
              <div key={field.key} className="space-y-2">
                <Label htmlFor={field.key}>
                  {field.label}
                  {field.required && <span className="text-destructive ml-1">*</span>}
                </Label>
                <div className="relative">
                  {field.type === 'select' ? (
                    <Select
                      value={formData.settings[field.key] || ''}
                      onValueChange={(value) => handleSettingChange(field.key, value)}
                      disabled={!canEdit}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={field.placeholder} />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options?.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="relative">
                      <Input
                        id={field.key}
                        type={field.type === 'password' && !showSecrets[field.key] ? 'password' : 'text'}
                        value={formData.settings[field.key] || ''}
                        onChange={(e) => handleSettingChange(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        disabled={!canEdit}
                      />
                      {field.type === 'password' && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3"
                          onClick={() => toggleSecretVisibility(field.key)}
                        >
                          {showSecrets[field.key] ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                {field.help_text && (
                  <p className="text-sm text-muted-foreground">{field.help_text}</p>
                )}
              </div>
            ))}

            {/* Test Connection Button */}
            {canEdit && (
              <div className="pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={testMutation.isPending || saveMutation.isPending}
                >
                  {testMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  {t('esigning.testConnection', 'Test Connection')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Built-in Provider Info */}
      {selectedProvider?.provider === 'builtin' && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>{t('esigning.builtinTitle', 'Using Built-in E-Signing')}</AlertTitle>
          <AlertDescription>
            {t('esigning.builtinDesc', 'The built-in e-signing solution requires no additional configuration. Contracts will be signed directly within Blueprint Desk with full audit trail and compliance features.')}
          </AlertDescription>
        </Alert>
      )}

      {/* Features List */}
      {selectedProvider && (
        <Card>
          <CardHeader>
            <CardTitle>{t('esigning.features', 'Features')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {selectedProvider.features.map((feature) => (
                <Badge key={feature} variant="outline">
                  <CheckCircle2 className="h-3 w-3 mr-1 text-green-500" />
                  {feature}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

